import React, { useEffect, useState , useMemo } from 'react'
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles'
import dayjs from 'dayjs';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useServices } from 'contexts';
import { getRecentCaseMasterAttempts } from 'services';
import { AttemptStates } from './constant';
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell';
import { NoResultComponent } from 'components/Home/TodoList';
import { Session } from 'utils';
import { useNavigateWithClient } from 'hooks';
import CustomButton from 'components/CustomButton';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import NoAccessTooltip, { NO_BATCH_ACCESS_MESSAGE } from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';
import { useCaseMasterAccess } from './CaseMasterAccessContext';
import CaseMasterLockMessage from './CaseMasterLockMessage';

const useStyles = makeStyles((theme) => ({
  box: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(5),
    marginTop: theme.spacing(5),
    maxHeight: '405px',
    overflow: 'auto',
    height: '405px',
    paddingRight: theme.spacing(3),
    overflowX: 'hidden'
  },
  cardContainer: {
    display: 'flex',
    borderRadius: theme.spacing(2),
    width: '100%',
  },
  category: {
    width: '82px',
    height: '82px',
    backgroundColor: theme.palette.neutral['clr-50'],
    borderRadius: theme.spacing(2, 0, 0, 2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  content: {
    padding: theme.spacing(3, 6),
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  topicDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  bottomContainer: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: 'center',
    marginTop: theme.spacing(2),
    background: `linear-gradient(360deg, #FFFFFF 80%, rgba(255, 255, 255, 0) 100%)`,
    position: 'relative',
    bottom: '0px',
  }
}));

export const AttemptCard = ({ attempt }) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigateWithClient = useNavigateWithClient();

  const removed = useSelectedBatchRemoved();

  const attemptState = attempt.status;
  const [startInterview, setStartInterview] = React.useState(false);
  const caseMaster = attempt?.casemasterProblem;

  // This card is an already-completed attempt, so a free user has spent their
  // single attempt and an expired Pro plan blocks reattempts. Batch removal
  // takes priority over the upgrade message.
  const { getLockInfo } = useCaseMasterAccess();
  const { locked, message: lockMessage, upgradeUrl, linkText } = getLockInfo(caseMaster?._id);
  const disabled = removed || locked;
  const tooltipMessage = removed
    ? NO_BATCH_ACCESS_MESSAGE
    : <CaseMasterLockMessage
      message={lockMessage} upgradeUrl={upgradeUrl} linkText={linkText}
    />;

  const colors = useMemo(() => {
    const borderColors = {
      [AttemptStates.FEEDBACK_SKIPPED]: '#BF7900',
      [AttemptStates.NOT_APPLICABLE]: '#E2483C',
    };

    const baseColor = borderColors[attemptState] || theme.palette.neutral['clr-200'];
    const hoverColor = borderColors[attemptState] || theme.palette.primary['clr-100'];

    return { baseColor, hoverColor };
  }, [attemptState, theme]);

  const getFeedbackMessage = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_SKIPPED:
        return 'Feedback isn\'t available for this attempt as the interview was not completed';
      case AttemptStates.NOT_APPLICABLE:
        return 'This attempt was ended before the interview started';
      default:
        return null;
    }
  };

  const FeedbackNote = () => {
    const message = getFeedbackMessage();
    if (!message) return null;

    let color;
    switch (attemptState) {
      case AttemptStates.FEEDBACK_SKIPPED:
        color = '#BF7900';
        break;
      case AttemptStates.NOT_APPLICABLE:
        color = '#E2483C';
        break;
      default:
        color = theme.palette.neutral['clr-700'];
    }

    return (
      <Typography variant='body01-bold' style={{ color }} component='span'>
        &nbsp;({message})
      </Typography>
    );
  };

  const RenderButtons = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_GENERATED:
        return (
          <>
            <NoAccessTooltip show={disabled} message={tooltipMessage}>
              <CustomButton
                onClick={() => setStartInterview(true)}
                disabled={disabled}
                startIcon={locked ? <LockOutlinedIcon sx={{ fontSize: '18px' }} /> : undefined}
              >
                Reattempt
              </CustomButton>
            </NoAccessTooltip>
            <CustomButton
              onClick={() => navigateWithClient(`/performance/case-master/${attempt._id}`)}
              variant='outlined'
            >
              View feedback
            </CustomButton>
          </>
        );
      case AttemptStates.FEEDBACK_SKIPPED:
      case AttemptStates.NOT_APPLICABLE:
        return null;
      default:
        return null;
    }
  };

  return (
    <Box
      className={classes.cardContainer}
      sx={{
        border: `2px solid ${colors.baseColor}`,
        '&:hover': {
          border: `2px solid ${colors.hoverColor}`,
        },
      }}
    >
      <Box className={classes.category}>
        <Box
          sx={{
            backgroundColor: theme.palette.shades['clr-white-900'],
            borderRadius: theme.spacing(3),
            width: 56,
            height: 56,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #CCD4DE'
          }}
        >
          <img src='https://assets.languify.in/images/caseIcon.png'  width={40} height={40}/>
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.topicDetails}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box display='flex' alignItems='center' gap='8px'>
            <Typography
              variant='h5-medium'
              color='neutral.clr-900'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                minWidth: 0,
                display: 'block',
              }}
            >
              {caseMaster?.title}
            </Typography>
          </Box>

          <Typography variant='body01-bold' color='neutral.clr-600'>
            Attempted on: {attemptState === AttemptStates.NOT_APPLICABLE
              ? 'N/A'
              : dayjs(attempt?.startedAt).format('DD/MM/YYYY')}
            <FeedbackNote />
          </Typography>
        </Box>

        <Box display='flex' gap='12px' alignItems='center'>
          <RenderButtons />
        </Box>
      </Box>
      <StartAssessmentDialog
        onClose={() => setStartInterview(false)}
        open={startInterview}
        template={{
          _id: attempt.casemasterProblem?._id,
          name: attempt.casemasterProblem?.title,
          type: 'case_master'
        }}
        codingInterview={false}
        stateParams={attempt.assessmentEntryPoint}
      />
    </Box>
  );
};

const RecentAttempts = ({ onAttemptStatusCheck }) => {
  const service = useServices();
  const classes = useStyles();

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRecentAttempts = async () => {
      try {
        setLoading(true);
        const result = await service.callService(getRecentCaseMasterAttempts, {
          include: "casemasterProblem",
          user: Session.userId
        });

        setAttempts(result);
        checkFirstFiveAttempts(result);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkFirstFiveAttempts = (attemptList) => {
    let hasPending = false;
    let hasSkipped = false;

    for (let i = 0; i < Math.min(5, attemptList.length); i++) {
      const attempt = attemptList[i];
      if (attempt.status === AttemptStates.FEEDBACK_SKIPPED) {
        hasSkipped = true;
      }
    }

    if (onAttemptStatusCheck) {
      onAttemptStatusCheck({ hasPending, hasSkipped });
    }
  };

  return (
    <WhiteBox>
      <WhiteBoxHeader
        id="recent-attempts"
        heading="Recent attempts"
        subheading="History of all your case interview attempts"
      />
      <Divider />
      <Box className={classes.box}>
        {
          loading
            ? Array.from({ length: 3 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  border: '2px solid #E3EAF3',
                  borderRadius: 2,
                  width: '100%',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: '82px',
                    height: '82px',
                    backgroundColor: '#F8F9FB',
                    borderRadius: '16px 0 0 16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 3,
                    ml: 3,
                  }}
                >
                  <Skeleton variant="circular" width={56} height={56} />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    padding: 3,
                    pl: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                    <Skeleton width="70%" height={24} sx={{ mb: 1 }} />
                    <Skeleton width="40%" height={20} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Skeleton variant="rectangular" width={120} height={36} />
                    <Skeleton variant="rectangular" width={120} height={36} />
                  </Box>
                </Box>
              </Box>
            ))
            :
            attempts?.length ? (
              attempts.map((attempt, index) => (
                <AttemptCard
                  key={index}
                  attempt={attempt}
                />
              ))
            ) : (
              <NoResultComponent
                description={
                  <>
                    Nothing to show here! <br />
                    Attempt any case interview challenge to see your progress.
                  </>
                }
                image='https://assets.languify.in/images/noPreparation.png'
              />
            )
        }
      </Box>
    </WhiteBox>
  );
};

export default RecentAttempts
